import type { ApiObject } from "@rudderstack/analytics-js";
import * as Sentry from "@sentry/browser";
import { isString } from "@sinch/feature-flags";
import { getPortalConfig } from "@sinch/shell-config";
import { locationAtom, prevLocationAtom } from "../../components/Router/state";
import { ENV } from "../../config/loadShellEnv";
import { getSessionId } from "../../featureFlags";
import { safeRetrieveValue } from "../../helpers/safeRetrieveValue";
import { getBrowserLang } from "../../intl/intl.utils";
import { globalJotaiStore } from "../../jotai-store";
import { getUserType } from "../../portals/dashboard/utils/getUserType";
import { getCountryCode } from "../../setCountryConfig";
import {
  getDeviceType,
  getWindowDimension,
  getOSName,
  getBrowserName,
  getBrowserVersion,
} from "../deviceInformation";
import { getRudderStackAnonymousId } from "../rudderstack";
import type { EventShellData, TrackEventPayload } from "../types";
import { getVendorId } from "../vendorId";
import {
  rudderStackClientEventZod,
  type RudderStackClientEvent,
} from "./eventTypes";

// Refers to https://confluence.sinch.com/pages/viewpage.action?pageId=290292852
export enum EVENT_TYPE {
  TEST_EVENT = "test.event",
  FRONTEND_EVENT = "frontend.event",
}

export enum EVENT_NAME {
  TEST_EVENT = "test_event",
  PAGE_LOADED = "page_loaded",
  USER_LOGIN = "user_logged_in",
  USER_LOGOUT = "user_logged_out",
}

const matchEventNameToEventType = (eventName: string) => {
  switch (eventName) {
    case EVENT_NAME.TEST_EVENT:
      return EVENT_TYPE.TEST_EVENT;
    default:
      return EVENT_TYPE.FRONTEND_EVENT;
  }
};

const getAppName = () => {
  const configName = getPortalConfig("configName");
  if (!configName) {
    return null;
  }

  const [entry] = configName.split(".");

  if (!entry) {
    return null;
  }

  switch (entry) {
    case "mm":
      return "Engage";
    case "engage":
      return "Legacy Engage";
    case "cop":
      return "COP";
    case "dashboard":
      return "Build";

    default:
      return null;
  }
};

export const createRudderStackClientEvent = async (
  payload: TrackEventPayload,
  shellData: EventShellData
): Promise<RudderStackClientEvent> => {
  const customInfo = payload.customInfo || payload.specificInfo;
  const { user } = shellData;
  const timestamp = Date.now();

  const countryCode = await getCountryCode();
  if (!countryCode) {
    Sentry.captureException(new Error("Country code not found"), {
      tags: {
        operation: "create_rudderstack_client_event",
      },
    });
  }

  const userHashedEmail = safeRetrieveValue(
    user?.settings ?? {},
    "hashedEmail",
    isString
  );

  const userType = user?.email ? getUserType(user.email) : null;

  const eventName = toSnakeCase(payload.appName + "_") + payload.name;

  const displayName =
    payload.appName
      .split("_")
      .filter((el) => el)
      .map(capitalizeFirstLetter)
      .join(" ") +
    " - " +
    payload.name.split("_").map(capitalizeFirstLetter).join(" ");

  return {
    event: {
      tracing_id: payload.id,
      type: `com.sinch.${matchEventNameToEventType(payload.name)}`,
      source: "com.sinch.frameworks",
      timestamp,
      session_id: getSessionId() || "",
      name: eventName,
      unformatted_name: payload.name || "",
      display_name: displayName,
      action: payload.action,
      tag_version: payload.tag || "",
      target: payload.target,
      description: payload.description || "",
    },
    user: {
      id: user.id || "",
      type: userType || user.type || "",
      email: userHashedEmail,
      anonymous_id: getRudderStackAnonymousId() ?? "",
      country: countryCode || user.account?.country || "",
      language: user.language ?? getBrowserLang() ?? "",
      role: user.roles || [],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      created_at: user.createdAt ?? "",
      has_phone_in_profile: user?.hasPhoneInProfile ?? null,
      is_super_user: user?.isSuperUser ?? null,
      is_smoke_tester: user?.isSmokeTester ?? null,
    },
    account: {
      id: user.account?.id ?? null,
      vendor_id: user.account?.vendorId ?? null,
      country: user.account?.country ?? null,
      payment_type: user.account?.paymentType ?? "",
      billing_type: user.account?.billingType ?? "",
      verification_status: user.account?.verificationStatus ?? "",
      currency: user.account?.currency ?? "",
      has_2fa_Enabled: user.account?.has2faEnabled ?? null,
      first_product: user.account?.firstProduct ?? "",
      focus_area: user.account?.focusArea ?? "",
      status: user.account?.status ?? "",
      type: user.account?.type ?? "",
      created_at: user.account?.createdAt ?? "",
    },
    device: {
      type: getDeviceType(),
      viewport_size: `${getWindowDimension().width}x${
        getWindowDimension().height
      }`,
      os: getOSName(),
      browser: getBrowserName(),
      browser_version: getBrowserVersion(),
      user_agent: navigator.userAgent,
    },
    app: {
      name: getAppName() ?? "",
      module: payload.appName,
      env: ENV,
      vendor_id: user.app?.vendorId ?? getVendorId() ?? "",
      region: rudderStackClientEventZod.shape.app.shape.region.parse(
        getPortalConfig("region")
      ),
    },
    page: {
      url: globalJotaiStore.get(locationAtom).href,
      prev_url: globalJotaiStore.get(prevLocationAtom)?.href || "",
    },
    experiment: {
      name: payload.experimentInfo?.experimentId || "",
      bucket: payload.experimentInfo?.bucketId || "",
    },
    custom: customInfo ? formatCustomProps(customInfo) : {},
  };
};

const toSnakeCase = (s: string) =>
  s
    // handle first uppercase
    .replace(/^([A-Z]+)/, (x) => x.toLowerCase())
    // handle uppercases that already have an underscore behind
    .replace(/_([A-Z]+)/g, (x) => x.toLowerCase())
    // handle other uppercases
    .replace(/([A-Z]+)/g, (x) => "_" + x.toLowerCase())
    // handle other separators
    .replace(/[\s.-]/g, "_")
    // handle multiple underscores
    .replace(/_+/g, "_");

const formatCustomProps = (
  customProps: ApiObject,
  isRoot = true
): ApiObject => {
  const toReturn: ApiObject = {};

  for (const i in customProps) {
    const currentValue = customProps[i];

    if (
      typeof currentValue == "object" &&
      !Array.isArray(currentValue) &&
      currentValue !== null
    ) {
      const flatObject = formatCustomProps(currentValue, false);

      for (const x in flatObject) {
        const key = toSnakeCase(`${isRoot ? "_" : ""}${i}_${x}`);
        toReturn[key] = flatObject[x];
      }
    } else {
      const key = toSnakeCase(`${isRoot ? "_" : ""}${i}`);
      toReturn[key] = currentValue;
    }
  }
  return toReturn;
};

const capitalizeFirstLetter = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);
